import React from 'react';
import { ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { HelpTooltip } from '../help/HelpTooltip';
import type { HelpTooltipContent } from '../help/HelpTooltip';
import { useTrackEvent } from '../../hooks/useTrackEvent';

/**
 * Period-over-period change chip data. Renders next to the card title as
 *   ↑ 2.3%  (green) — improvement
 *   ↓ 0.5%  (red) — regression
 *   — 0%    (gray) — no change
 * The arrow direction is always the literal sign of `value`; the COLOUR
 * depends on `isInverse` (some metrics are good when they go down, e.g.
 * Run-Off Rate, Inactive Loan Balance).
 */
export interface StatCardChange {
  /** Numeric delta. Sign drives the arrow direction; absolute value is rendered. */
  value: number;
  /** Display unit. '%' (default) for relative change, 'pp' for percentage points (used by run-off rate which is itself a %). */
  unit?: '%' | 'pp';
  /**
   * Inverse semantics — set true when a NEGATIVE change is good
   * (e.g. lower run-off rate = healthier, lower inactive balance = healthier).
   * When false (default), positive change is green.
   */
  isInverse?: boolean;
  /** Comparison label (e.g. "vs prev 12 months"). Hidden on narrow viewports to save space. */
  label?: string;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  subtitle?: string;
  periodLabel?: string; // Replaces "For selected period" when provided
  gradientColor?: string;
  isUpdating?: boolean; // ✅ NEW: Show "Updating..." badge during aggregation
  // ✅ NEW (2025-11-16): Data quality indicators
  dataQualityScore?: number; // 0-100 quality score
  lastValidated?: Date | null; // Last validation timestamp
  validationConfidence?: 'HIGH' | 'MEDIUM' | 'LOW' | null; // Confidence level
  // ✅ NEW (2026-05-12): Optional inline help tooltip next to the title.
  // Pass `helpKey` for telemetry (matches CardHelp.key in helpContent.ts) and
  // `helpTooltip` (the definition+formula content from cardHelp(...)).
  helpKey?: string;
  helpTooltip?: HelpTooltipContent;
  /** Page key for telemetry namespacing (e.g. 'trailRunOff'). Only used when helpTooltip is set. */
  helpPageKey?: string;
  /** Period-over-period delta chip data. See StatCardChange. */
  change?: StatCardChange | null;
  /**
   * Native tooltip on the headline value. Use it to carry the exact figure when
   * `value` is a compact form (e.g. value "$1.29B", valueTitle "$1,292,424,853").
   */
  valueTitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  subtitle,
  periodLabel,
  gradientColor = '#3b82f6',
  isUpdating = false,
  dataQualityScore,
  lastValidated,
  validationConfidence,
  helpKey,
  helpTooltip,
  helpPageKey,
  change,
  valueTitle,
}) => {
    const { trackEvent } = useTrackEvent();

    // Period-over-period chip rendering. Direction is the sign of the value;
    // colour reflects "is this good or bad" via isInverse.
    const renderChangeChip = () => {
        if (!change || change.value === null || change.value === undefined || !isFinite(change.value)) {
            return null;
        }
        const unit = change.unit ?? '%';
        const isInverse = !!change.isInverse;
        const value = change.value;
        const abs = Math.abs(value);
        const isZero = abs < 0.05; // round-to-display-zero
        const isPositive = value > 0;
        const isImprovement = isZero ? false : (isInverse ? !isPositive : isPositive);

        const colour = isZero
            ? 'bg-gray-700/40 text-gray-400 border-gray-600/40'
            : isImprovement
                ? 'bg-green-500/15 text-green-400 border-green-500/30'
                : 'bg-red-500/15 text-red-400 border-red-500/30';

        const ArrowIcon = isZero ? Minus : (isPositive ? ArrowUp : ArrowDown);
        const formatted = unit === 'pp' ? `${abs.toFixed(1)} pp` : `${abs.toFixed(1)}%`;

        return (
            <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-medium ${colour}`}
                title={change.label ? `${formatted} ${change.label}` : formatted}
            >
                <ArrowIcon size={12} aria-hidden="true" />
                <span>{formatted}</span>
                {change.label && (
                    <span className="hidden sm:inline text-[0.6875rem] opacity-80 ml-0.5">{change.label}</span>
                )}
            </span>
        );
    };

    // Helper function to convert hex to RGB
    const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    };

    // Create a stronger gradient effect
    const getGradientStyle = () => {
        return {
            background: `linear-gradient(135deg, rgba(31, 41, 55, 0.8) 0%, rgba(31, 41, 55, 0.95) 50%, rgba(17, 24, 39, 1) 100%)`,
        };
    };

    const getColoredGradientStyle = () => {
        const rgb = hexToRgb(gradientColor);
        if (rgb) {
            return {
                background: `linear-gradient(135deg, 
                    rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15) 0%, 
                    rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.05) 50%, 
                    rgba(17, 24, 39, 0.8) 100%)`
            };
        }
        return {};
    };

    // ✅ NEW (2025-11-16): Data quality helpers
    const getQualityBadgeColor = () => {
        if (!dataQualityScore) return null;
        if (dataQualityScore >= 99) return { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30', label: 'Verified' };
        if (dataQualityScore >= 95) return { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30', label: 'Good' };
        return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', label: 'Warning' };
    };

    const getConfidenceBadge = () => {
        if (!validationConfidence) return null;
        const badges: Record<'HIGH' | 'MEDIUM' | 'LOW', { icon: string; color: string; label: string }> = {
            HIGH: { icon: '✓', color: 'text-green-400', label: '100% Confidence' },
            MEDIUM: { icon: '~', color: 'text-yellow-400', label: '~95% Confidence' },
            LOW: { icon: '!', color: 'text-red-400', label: 'Low Confidence' },
        };
        return badges[validationConfidence as 'HIGH' | 'MEDIUM' | 'LOW'];
    };

    const formatLastValidated = () => {
        if (!lastValidated) return null;
        const now = new Date();
        const diff = now.getTime() - lastValidated.getTime();
        const minutes = Math.floor(diff / 60000);
        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
    };

    const qualityBadge = getQualityBadgeColor();
    const confidenceBadge = getConfidenceBadge();

    return (
        <div className="relative rounded-lg shadow-xl overflow-hidden group hover:shadow-2xl transition-all duration-300">
            {/* Base dark gradient background */}
            <div
                className="absolute inset-0"
                style={getGradientStyle()}
            />

            {/* Colored gradient overlay */}
            <div
                className="absolute inset-0 opacity-60"
                style={getColoredGradientStyle()}
            />

            {/* Top accent gradient line */}
            <div
                className="absolute top-0 left-0 right-0 h-1"
                style={{
                    background: `linear-gradient(90deg,
                        transparent 0%,
                        ${gradientColor} 15%,
                        ${gradientColor} 85%,
                        transparent 100%)`
                }}
            />

            {/* Content */}
            <div className="relative p-4 md:p-6 z-10">
                <div className="flex justify-between items-start">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <p className="text-gray-400 text-sm font-medium uppercase tracking-wider">
                                {title}
                            </p>
                            {helpTooltip && (
                                <HelpTooltip
                                    content={helpTooltip}
                                    ariaLabel={`Help: ${title}`}
                                    onOpen={() => {
                                        if (helpKey) {
                                            trackEvent('help_card_tooltip_opened', {
                                                pageKey: helpPageKey,
                                                cardKey: helpKey,
                                                deviceClass: typeof window !== 'undefined'
                                                    ? (window.innerWidth < 640 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop')
                                                    : 'desktop',
                                            });
                                        }
                                    }}
                                />
                            )}
                            {/* ✅ NEW (2026-05-12): Period-over-period chip */}
                            {renderChangeChip()}
                            {/* ✅ NEW: Updating badge (stale-while-revalidate pattern) */}
                            {isUpdating && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 animate-pulse">
                                    Updating...
                                </span>
                            )}
                            {/* ✅ NEW (2025-11-16): Quality badge */}
                            {qualityBadge && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${qualityBadge.bg} ${qualityBadge.text} border ${qualityBadge.border}`}>
                                    {qualityBadge.label}
                                </span>
                            )}
                            {/* ✅ NEW (2025-11-16): Confidence badge */}
                            {confidenceBadge && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${confidenceBadge.color}`} title="Data validation confidence">
                                    {confidenceBadge.icon} {confidenceBadge.label}
                                </span>
                            )}
                        </div>
                        <p className={`text-2xl md:text-3xl lg:text-4xl font-bold text-white transition-opacity duration-300 ${isUpdating ? 'opacity-75' : 'opacity-100'}`}>
                            <span data-ph-mask title={valueTitle}>{value}</span>
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                            <p className="text-xs text-gray-500">
                                {periodLabel || 'For selected period'}
                                {subtitle && <span className="ml-1">• {subtitle}</span>}
                            </p>
                            {/* ✅ NEW (2025-11-16): Last validated timestamp */}
                            {lastValidated && (
                                <span className="text-xs text-gray-600" title={`Last validated: ${lastValidated.toLocaleString()}`}>
                                    • Validated {formatLastValidated()}
                                </span>
                            )}
                        </div>
                    </div>
                    {icon && (
                        <div
                            className="ml-4 p-3 rounded-xl"
                            style={{
                                background: `linear-gradient(135deg,
                                    ${gradientColor}30 0%,
                                    ${gradientColor}10 100%)`,
                                boxShadow: `0 0 20px ${gradientColor}20`
                            }}
                        >
                            <div style={{ color: gradientColor }}>
                                {icon}
                            </div>
                        </div>
                    )}
                </div>

                {/* Bottom gradient glow */}
                <div
                    className="absolute bottom-0 left-0 right-0 h-20 -z-10 opacity-20 blur-xl"
                    style={{
                        background: `radial-gradient(ellipse at center bottom, ${gradientColor}, transparent)`
                    }}
                />
            </div>
        </div>
    );
};

export default StatCard;